import React, { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Pane, Icon, Button } from '@folio/stripes/components';
/** FOLIO root — module routes are each guarded, so the module root could deny access again. */
var HOME_PATH = '/';
/**
 * How long "Back" waits for the history to move before it concludes there was no previous
 * page and goes home instead. A browser answers a successful back with popstate well within it.
 */
export var BACK_FALLBACK_DELAY_MS = 300;
/**
 * Identity of a history entry. The initial browser entry carries no key, so its address
 * stands in for it.
 */
var entryIdentity = function (_a) {
    var key = _a.key, pathname = _a.pathname, search = _a.search, hash = _a.hash;
    return (key !== null && key !== void 0 ? key : "".concat(pathname).concat(search).concat(hash));
};
/**
 * Access denied component shown when user lacks required permissions.
 * Offers a way out: back to the previous page, or to the FOLIO home page (CZNA-1628).
 */
export var AccessDenied = function () {
    var history = useHistory();
    var fallbackTimer = useRef(null);
    var clearFallback = useCallback(function () {
        if (fallbackTimer.current !== null) {
            clearTimeout(fallbackTimer.current);
            fallbackTimer.current = null;
        }
    }, []);
    // An in-app back unmounts this page; the pending fallback must not fire after it.
    useEffect(function () { return clearFallback; }, [clearFallback]);
    var goHome = useCallback(function () {
        clearFallback();
        history.push(HOME_PATH);
    }, [history, clearFallback]);
    // Neither the length of the history nor (in a browser) its index proves there is a
    // BACKWARD entry: a page opened directly can still have a forward one. So go back and
    // watch: if the location has not moved, there was nothing to go back to and the button
    // leads home instead of being a dead end.
    var goBack = useCallback(function () {
        clearFallback();
        var before = entryIdentity(history.location);
        history.goBack();
        fallbackTimer.current = setTimeout(function () {
            fallbackTimer.current = null;
            if (entryIdentity(history.location) === before) {
                history.push(HOME_PATH);
            }
        }, BACK_FALLBACK_DELAY_MS);
    }, [history, clearFallback]);
    return (React.createElement(Pane, { defaultWidth: "fill", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-users.accessDenied.title", defaultMessage: "Access Denied" }) },
        React.createElement("div", { style: { textAlign: 'center', padding: '40px' } },
            React.createElement(Icon, { icon: "exclamation-circle", size: "large" }),
            React.createElement("h2", null,
                React.createElement(FormattedMessage, { id: "ui-czna-users.accessDenied.heading", defaultMessage: "You don't have permission to view this page" })),
            React.createElement("p", null,
                React.createElement(FormattedMessage, { id: "ui-czna-users.accessDenied.message", defaultMessage: "Please contact your administrator if you believe this is an error." })),
            React.createElement("div", null,
                React.createElement(Button, { onClick: goBack },
                    React.createElement(FormattedMessage, { id: "ui-czna-users.accessDenied.back", defaultMessage: "Back" })),
                React.createElement(Button, { buttonStyle: "primary", onClick: goHome },
                    React.createElement(FormattedMessage, { id: "ui-czna-users.accessDenied.home", defaultMessage: "Go to home page" }))))));
};
export default AccessDenied;
